import { useCallback, useRef, useState } from "react";
import { intakeFile, intakeBytes, IntakeError } from "@jdoc/document-model";
import { Spinner } from "@jdoc/ui";
import { useWorkspace } from "@/state/workspaceStore";
import { pickFileNative } from "@/lib/fileSystem";
import { isEncryptedPdf, decryptPdf, PdfDecryptError } from "@/lib/pdfDecrypt";
import { IconFile, IconFolder } from "@/components/icons";

const ACCEPT =
  ".pdf,.docx,.odt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.oasis.opendocument.text";

const FILE_REQUIREMENT = "PDF, DOCX or ODT up to 100MB";

/**
 * The file-opening surface shared by the desktop and mobile landing layouts.
 *
 * One `handleFile` validates both the picker and drag-and-drop paths (magic-byte
 * intake in `@jdoc/document-model`); nothing is uploaded. On desktop it invites
 * drag-and-drop; on mobile it leads with a full-width "Choose a file" button.
 */
export function FileDropZone({ variant }: { variant: "desktop" | "mobile" }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const openDocument = useWorkspace((s) => s.openDocument);
  const setError = useWorkspace((s) => s.setError);
  const setBusy = useWorkspace((s) => s.setBusy);
  const busy = useWorkspace((s) => s.busy);
  const error = useWorkspace((s) => s.error);

  const handleFile = useCallback(
    async (file: File | undefined, handle?: FileSystemFileHandle) => {
      if (!file) return;
      setBusy(true);
      setError(null);
      try {
        let doc = await intakeFile(file);
        if (doc.kind === "pdf" && isEncryptedPdf(doc.bytes)) {
          const decrypted = await decryptPdf(doc.bytes);
          doc = intakeBytes(decrypted, doc.displayName);
        }
        openDocument(doc, handle);
      } catch (err) {
        const message =
          err instanceof PdfDecryptError
            ? err.message
            : err instanceof IntakeError
              ? err.message
              : "This file could not be opened. Choose a valid PDF, DOCX or ODT file up to 100MB.";
        setError(message);
      } finally {
        setBusy(false);
        // Allow re-selecting the same file after closing/erroring.
        if (inputRef.current) inputRef.current.value = "";
      }
    },
    [openDocument, setBusy, setError],
  );

  const onOpenClick = async () => {
    try {
      const res = await pickFileNative();
      if (res) {
        await handleFile(res.file, res.handle);
        return;
      }
    } catch {
      // fall through to the standard input picker
    }
    inputRef.current?.click();
  };

  const isMobile = variant === "mobile";

  return (
    <div className="w-full">
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          void handleFile(e.dataTransfer.files[0]);
        }}
        className={[
          "flex flex-col items-center justify-center rounded-2xl border-2 border-dashed px-6 text-center transition-colors",
          isMobile ? "py-8" : "min-h-[150px] py-4",
          dragging
            ? "border-brand-500 bg-brand-50/60"
            : "border-brand-200 bg-slate-50/60",
        ].join(" ")}
      >
        <div
          className={[
            "flex items-center justify-center rounded-full bg-brand-50 text-brand-600",
            isMobile ? "h-14 w-14" : "h-10 w-10",
          ].join(" ")}
        >
          {busy ? (
            <Spinner className={isMobile ? "h-6 w-6" : "h-4 w-4"} />
          ) : (
            <IconFile className={isMobile ? "h-7 w-7 stroke-[1.75]" : "h-5 w-5 stroke-[1.75]"} />
          )}
        </div>

        {!isMobile && (
          <>
            <p className="mt-2 text-base font-bold text-slate-900">
              Drag &amp; drop your file here
            </p>
            <p className="mt-0.5 text-sm text-slate-400">or</p>
          </>
        )}

        <button
          type="button"
          disabled={busy}
          onClick={() => void onOpenClick()}
          className={[
            "inline-flex items-center justify-center gap-2 rounded-xl bg-brand-600 font-semibold text-white shadow-sm transition-colors hover:bg-brand-700 active:bg-brand-800 disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600",
            isMobile ? "mt-4 min-h-[52px] w-full px-6" : "mt-1.5 px-6 py-2",
          ].join(" ")}
        >
          <IconFolder className="h-5 w-5" aria-hidden="true" />
          Choose a file
        </button>

        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          aria-label="Choose a PDF, DOCX or ODT file to open"
          className="sr-only"
          onChange={(e) => void handleFile(e.target.files?.[0])}
        />

        <p className={isMobile ? "mt-4 text-sm text-slate-500" : "mt-1.5 text-sm text-slate-500"}>
          {FILE_REQUIREMENT}
        </p>
      </div>

      <p aria-live="polite" className="min-h-0">
        {error && (
          <span
            role="alert"
            className="mt-3 block rounded-md bg-red-50 px-3 py-2 text-center text-sm text-red-700"
          >
            {error}
          </span>
        )}
      </p>
    </div>
  );
}
